import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AbstractControlDirective } from '@supoui/shared/components/abstract-control.directive';
import { LocationService } from '@supoui/core/services/location.service';
import { Location } from '@supoui/shared/model/location';
import Autocomplete = google.maps.places.Autocomplete;

@Component({
    selector: 'app-input-location',
    templateUrl: './input-location.component.html',
    styleUrls: ['./input-location.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            useExisting: forwardRef(() => InputLocationComponent),
            multi: true
        }
    ],
    standalone: false
})
export class InputLocationComponent extends AbstractControlDirective implements AfterViewInit {
  public locationControl: UntypedFormControl = new UntypedFormControl();

  @ViewChild('searchElement', { static: false }) public searchElementLocationRef: ElementRef;
  value: Location = new Location();

  @Input() allowEmpty = false;
  @Input() invalid = false;

  isValid = false;
  isValidAddress = false;
  locationSetByGoogle: boolean;

  constructor(private locationService: LocationService) {
    super();
    this.subscriptions.push(
      this.locationControl.valueChanges.subscribe(value => {
        this.locationSetByGoogle = false;
        this.markIfLocationIsValid();
        this.onChange(this.value);
      })
    );
  }

  ngAfterViewInit(): void {
    const autocomplete = new Autocomplete(this.searchElementLocationRef.nativeElement, {}); // types: ['geocode']
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      this.getInformationFromGoogle(place);
      this.value.freeText = this.searchElementLocationRef.nativeElement.value;
      this.markIfLocationIsValid();
      this.onChange(this.value);
      this.onTouched();
    });
  }

  public writeValue(value: Location | null): void {
    value = value === undefined ? null : value;
    this.locationSetByGoogle = !!value?.latitude && !!value.longitude;
    this.locationControl.patchValue(value?.freeText, { emitEvent: false });
    this.validate();
  }

  public setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.locationControl.disable({ emitEvent: false });
    } else {
      this.locationControl.enable({ emitEvent: false });
    }
  }

  private getInformationFromGoogle(place: any): void {
    this.locationSetByGoogle = false;
    if (place == null) {
      this.clearLocation();
      return;
    }
    if (!place.geometry) {
      return;
    }

    if (place.address_components === undefined) {
      this.clearLocation();
      this.locationSetByGoogle = false;
      return;
    }

    this.clearLocation();
    this.locationSetByGoogle = true;
    for (const address of place.address_components) {
      switch (address.types[0]) {
        case 'street_number':
          this.value.streetNumber = address.long_name;
          break;
        case 'route':
          this.value.streetName = address.long_name;
          break;
        case 'locality':
          this.value.locality = address.long_name;
          break;
        case 'administrative_area_level_1':
          this.value.administrativeAreaLevel1 = address.long_name;
          break;
        case 'administrative_area_level_2':
          this.value.administrativeAreaLevel2 = address.long_name;
          break;
        case 'country':
          this.value.country = address.long_name;
          this.value.countryCodeAlpha2 = address.short_name;
          break;
        case 'postal_code':
          this.value.postalCode = address.long_name;
          break;
      }
    }

    this.value.latitude = place.geometry.location.lat();
    this.value.longitude = place.geometry.location.lng();
    this.value.utcOffsetInHours = place.utc_offset_minutes ? place.utc_offset_minutes / 60 : 0;

    this.locationService.requestTimeZoneId(this.value.latitude, this.value.longitude).subscribe(result => {
      this.value.timeZoneId = result.timeZoneId;
      this.onChange(this.value);
      this.onTouched();
    });
  }

  private markIfLocationIsValid(): void {
    this.isValid = false;
    this.isValidAddress = false;
    if (this.locationSetByGoogle) {
      this.validate();
    } else {
      this.clearLocation();
      this.value.freeText = this.searchElementLocationRef.nativeElement.value;
    }
  }

  private validate(): void {
    this.validateLocation();
    this.validateAddress();
  }

  private validateLocation(): void {
    this.isValid = false;
    if (this.allowEmpty && this.value.freeText === '') {
      this.isValid = true;
    } else if (this.value.locality) {
      this.isValid = true;
    } else if (this.value.administrativeAreaLevel1 !== undefined && this.value.administrativeAreaLevel1 !== '') {
      this.isValid = true;
    }
  }

  private validateAddress(): void {
    this.isValidAddress = false;
    if (this.allowEmpty && this.value.freeText === '') {
      this.isValidAddress = true;
    } else if (this.value.country && this.value.streetName && this.value.streetNumber) {
      this.isValidAddress = true;
    }
  }

  private clearLocation(): void {
    this.locationSetByGoogle = false;
    this.value.streetNumber = '';
    this.value.streetName = '';
    this.value.locality = '';
    this.value.administrativeAreaLevel1 = '';
    this.value.administrativeAreaLevel2 = '';
    this.value.country = '';
    this.value.postalCode = '';
    this.value.latitude = 0;
    this.value.longitude = 0;
    this.value.timeZoneId = '';
    this.value.utcOffsetInHours = 0;
    this.isValid = false;
    this.value.countryCodeAlpha2 = '';
  }
}
